import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FiLink, FiWifi, FiUser, FiFileText, FiMail, FiMessageSquare,
  FiPhone, FiMapPin, FiCalendar, FiUpload, FiDownload, FiX,
  FiArrowLeft, FiArrowRight,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import styles from "./QrToolPage.module.css";
import ToolFeedback from "../toolFeedback/ToolFeedback";

const SITE = "https://www.danielrayrangel.com";
const PREVIEW_SIZE = 320;

/* ────────────────────────────────────────────────────────────────────────
   Content encoders — each turns the current form values into the exact
   string a QR scanner expects. Escaping matters: a stray ; or , in a Wi-Fi
   password or vCard field silently corrupts the code, so escape per spec.
   ──────────────────────────────────────────────────────────────────────── */

const escWifi = (s) => String(s ?? "").replace(/([\\;,:"])/g, "\\$1");
const escVcard = (s) =>
  String(s ?? "").replace(/([\\;,])/g, "\\$1").replace(/\n/g, "\\n");

const withHttp = (u) =>
  /^[a-z][a-z0-9+.-]*:/i.test(u) ? u : `https://${u}`;

// datetime-local ("2026-07-25T09:00") → iCalendar basic ("20260725T090000")
const icalDate = (s) => (s ? s.replace(/[-:]/g, "") + "00" : "");

function buildWifi(v) {
  const enc = v.encryption || "WPA";
  if (enc === "nopass") return `WIFI:S:${escWifi(v.ssid)};T:nopass;;`;
  return `WIFI:S:${escWifi(v.ssid)};T:${enc};P:${escWifi(v.password || "")};H:${
    v.hidden ? "true" : "false"
  };;`;
}

function buildVcard(v) {
  const fn = [v.first, v.last].filter(Boolean).join(" ");
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${escVcard(v.last)};${escVcard(v.first)};;;`,
    `FN:${escVcard(fn)}`,
  ];
  if (v.org) lines.push(`ORG:${escVcard(v.org)}`);
  if (v.title) lines.push(`TITLE:${escVcard(v.title)}`);
  if (v.phone) lines.push(`TEL;TYPE=CELL:${escVcard(v.phone)}`);
  if (v.email) lines.push(`EMAIL:${escVcard(v.email)}`);
  if (v.website) lines.push(`URL:${escVcard(withHttp(v.website))}`);
  if (v.street || v.city || v.state || v.zip || v.country) {
    lines.push(
      `ADR;TYPE=WORK:;;${escVcard(v.street)};${escVcard(v.city)};${escVcard(
        v.state
      )};${escVcard(v.zip)};${escVcard(v.country)}`
    );
  }
  lines.push("END:VCARD");
  return lines.join("\n");
}

function buildEvent(v) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `SUMMARY:${escVcard(v.title)}`,
  ];
  if (v.location) lines.push(`LOCATION:${escVcard(v.location)}`);
  if (v.start) lines.push(`DTSTART:${icalDate(v.start)}`);
  if (v.end) lines.push(`DTEND:${icalDate(v.end)}`);
  if (v.description) lines.push(`DESCRIPTION:${escVcard(v.description)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\n");
}

function encode(type, v = {}) {
  switch (type) {
    case "url":
      return v.url ? withHttp(v.url.trim()) : "";
    case "text":
      return v.text || "";
    case "email": {
      if (!v.to) return "";
      const q = [];
      if (v.subject) q.push(`subject=${encodeURIComponent(v.subject)}`);
      if (v.body) q.push(`body=${encodeURIComponent(v.body)}`);
      return `mailto:${v.to}${q.length ? "?" + q.join("&") : ""}`;
    }
    case "phone":
      return v.phone ? `tel:${v.phone.replace(/[^\d+]/g, "")}` : "";
    case "sms":
      return v.phone ? `SMSTO:${v.phone.replace(/[^\d+]/g, "")}:${v.message || ""}` : "";
    case "whatsapp": {
      const digits = (v.phone || "").replace(/\D/g, "");
      if (!digits) return "";
      return `https://wa.me/${digits}${
        v.message ? `?text=${encodeURIComponent(v.message)}` : ""
      }`;
    }
    case "wifi":
      return v.ssid ? buildWifi(v) : "";
    case "geo":
      return v.lat && v.lng ? `geo:${v.lat},${v.lng}` : "";
    case "event":
      return v.title && v.start ? buildEvent(v) : "";
    case "vcard":
      return v.first || v.last || v.org || v.phone || v.email
        ? buildVcard(v)
        : "";
    default:
      return "";
  }
}

/* ── Static config ─────────────────────────────────────────────────────── */

const TYPES = [
  { id: "url", label: "Website", icon: FiLink },
  { id: "wifi", label: "Wi-Fi", icon: FiWifi },
  { id: "vcard", label: "Business Card", icon: FiUser },
  { id: "text", label: "Text", icon: FiFileText },
  { id: "email", label: "Email", icon: FiMail },
  { id: "sms", label: "SMS", icon: FiMessageSquare },
  { id: "phone", label: "Phone", icon: FiPhone },
  { id: "whatsapp", label: "WhatsApp", icon: FaWhatsapp },
  { id: "geo", label: "Location", icon: FiMapPin },
  { id: "event", label: "Event", icon: FiCalendar },
];

const DOT_STYLES = [
  { v: "square", l: "Square" },
  { v: "rounded", l: "Rounded" },
  { v: "dots", l: "Dots" },
  { v: "classy", l: "Classy" },
  { v: "classy-rounded", l: "Classy R." },
  { v: "extra-rounded", l: "Extra R." },
];
const CORNER_SQUARE_STYLES = [
  { v: "square", l: "Square" },
  { v: "dot", l: "Dot" },
  { v: "extra-rounded", l: "Rounded" },
];
const CORNER_DOT_STYLES = [
  { v: "square", l: "Square" },
  { v: "dot", l: "Dot" },
];
const EC_LEVELS = [
  { v: "L", l: "L" },
  { v: "M", l: "M" },
  { v: "Q", l: "Q" },
  { v: "H", l: "H" },
];
const EXPORT_SIZES = [512, 1024, 2048];

const USE_CASES = [
  {
    icon: FiWifi,
    title: "Restaurant & shop Wi-Fi",
    body: "Print it on a table tent. Guests scan and connect - no password typed, no staff interrupted.",
  },
  {
    icon: FiUser,
    title: "Digital business card",
    body: "One scan drops your name, number, and email straight into their phone's contacts. Never run out of cards again.",
  },
  {
    icon: FiLink,
    title: "Google review link",
    body: "Point customers at your review page while they're still happy. More five-stars, less friction.",
  },
  {
    icon: FiMapPin,
    title: "Find-us pin",
    body: "Drop your exact coordinates on flyers or packaging so people navigate to your door in one tap.",
  },
];

const FAQS = [
  {
    q: "Is it really free and unlimited?",
    a: "Yes. Generate and download as many codes as you want, forever. No account, no watermark you can't remove, no daily cap.",
  },
  {
    q: "Do these codes ever expire?",
    a: "No. Every code here is static - the information is baked directly into the pattern, so it works as long as the code is scannable. Nothing to renew and nothing that can be shut off.",
  },
  {
    q: "Will adding a logo break the scan?",
    a: "Not if you keep it centered and reasonably sized. Whenever a logo is present the tool automatically bumps error correction to its highest level so scanners can read around it. Always test-scan before printing.",
  },
  {
    q: "Is my data sent anywhere?",
    a: "No. Everything is generated inside your browser. Your Wi-Fi password, contact details, and links never touch a server — mine or anyone else's.",
  },
  {
    q: "Can I edit where a code points after I print it?",
    a: "Not with a static code. If you need a code whose destination you can change later — or one that tracks scans — that's a dynamic code, which needs hosting behind it. That's something I build. Reach out below.",
  },
];

const BACK_LINK = "More free tools";

/* Feedback from inside the tool, the same way the tip splitter takes it: a
 * modal over the page rather than a trip to a contact form, because the person
 * with something to say is looking at the code that wouldn't scan.
 *
 * Copy lives here as a module const rather than in a strings file, since this
 * page inlines all of its copy (TYPES, USE_CASES, FAQS above). It moves to a
 * strings file when the rest of them do.
 */
const FEEDBACK = {
  eyebrow: "Still being built",
  text: "If a code wouldn't scan, or the kind you needed isn't in the list, tell me. Most of it is a quick fix.",
  button: "Send feedback",
  form: {
    eyebrow: "QR Code Generator",
    title: "What should this do differently?",
    sub: "A code that failed on a real scanner, a type that isn't here, a size or format you needed. Tell me what you were trying to print. It comes straight to me.",
    name: "Name",
    contact: "Email or phone",
    contactPlaceholder: "you@example.com or (480) 555-0123",
    message: "What happened",
    messagePlaceholder:
      "The Wi-Fi code works on my phone but the front desk iPad will not read it...",
    send: "Send it",
    sending: "Sending...",
    cancel: "Cancel",
    close: "Close",
    invalid: "Needs a name, a way to reach you, and a note.",
    error: "That didn't send. Try again, or text (480) 282-0569.",
    sent: "Got it, thanks. If I have a follow-up question I'll reach out at what you left.",
    backToTool: "Back to the generator",
    privacy:
      "Your name and contact come to me and nowhere else. Nothing you put into the generator is attached.",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ── Download helpers ──────────────────────────────────────────────────── */

const loadImg = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const QrToolPage = () => {
  const [type, setType] = useState("url");
  const [values, setValues] = useState({});

  const [dotStyle, setDotStyle] = useState("rounded");
  const [cornerSquareStyle, setCornerSquareStyle] = useState("extra-rounded");
  const [cornerDotStyle, setCornerDotStyle] = useState("dot");

  const [dotColor, setDotColor] = useState("#1a1a1a");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [transparentBg, setTransparentBg] = useState(false);
  const [accentCorners, setAccentCorners] = useState(true);
  const [cornerColor, setCornerColor] = useState("#d9561e");

  const [ecLevel, setEcLevel] = useState("Q");
  const [logo, setLogo] = useState(null);
  const [logoSize, setLogoSize] = useState(0.4);
  const [hideBgDots, setHideBgDots] = useState(true);

  const [branding, setBranding] = useState(true);
  const [exportSize, setExportSize] = useState(1024);
  const [format, setFormat] = useState("png");
  const [busy, setBusy] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const feedbackBtnRef = useRef(null);

  const cur = values[type] || {};
  const set = useCallback(
    (name, val) =>
      setValues((v) => ({ ...v, [type]: { ...v[type], [name]: val } })),
    [type]
  );

  const data = useMemo(() => encode(type, values[type]), [type, values]);
  const previewData = data || SITE;
  const effectiveBg = transparentBg ? "rgba(0,0,0,0)" : bgColor;
  const cornersColor = accentCorners ? cornerColor : dotColor;
  const captionColor = dotColor;

  const buildOptions = useCallback(
    (size, renderType) => ({
      width: size,
      height: size,
      type: renderType,
      data: previewData,
      margin: 10,
      qrOptions: { errorCorrectionLevel: logo ? "H" : ecLevel },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 6,
        imageSize: logoSize,
        hideBackgroundDots: hideBgDots,
      },
      dotsOptions: { type: dotStyle, color: dotColor },
      backgroundOptions: { color: effectiveBg },
      cornersSquareOptions: { type: cornerSquareStyle, color: cornersColor },
      cornersDotOptions: { type: cornerDotStyle, color: cornersColor },
      image: logo || undefined,
    }),
    [
      previewData, logo, ecLevel, logoSize, hideBgDots, dotStyle, dotColor,
      effectiveBg, cornersColor, cornerSquareStyle, cornerDotStyle,
    ]
  );

  const previewRef = useRef(null);
  const qrRef = useRef(null);
  const ctorRef = useRef(null);

  // qr-code-styling touches the DOM/canvas, so it's dynamically imported —
  // this keeps ~50KB out of every other page's bundle and off the main thread
  // until someone actually opens the tool.
  useEffect(() => {
    let cancelled = false;
    import("qr-code-styling").then((mod) => {
      if (cancelled) return;
      ctorRef.current = mod.default;
      qrRef.current = new mod.default(buildOptions(PREVIEW_SIZE, "svg"));
      if (previewRef.current) {
        previewRef.current.innerHTML = "";
        qrRef.current.append(previewRef.current);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (qrRef.current) qrRef.current.update(buildOptions(PREVIEW_SIZE, "svg"));
  }, [buildOptions]);

  const onLogo = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  async function brand(blob, size, fmt) {
    const capH = Math.round(size * 0.085);
    const stripBg = transparentBg ? "#ffffff" : bgColor;
    const caption = "Made with danielrayrangel.com";

    if (fmt === "svg") {
      const svg = await blob.text();
      const total = size + capH;
      const out =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${total}" viewBox="0 0 ${size} ${total}">` +
        `<rect x="0" y="${size}" width="${size}" height="${capH}" fill="${stripBg}"/>` +
        svg.replace("<svg", '<svg x="0" y="0"') +
        `<text x="${size / 2}" y="${size + capH * 0.62}" font-family="'JetBrains Mono', monospace" font-size="${Math.round(
          capH * 0.4
        )}" fill="${captionColor}" text-anchor="middle" letter-spacing="0.5">${caption}</text>` +
        `</svg>`;
      return new Blob([out], { type: "image/svg+xml" });
    }

    const url = URL.createObjectURL(blob);
    try {
      const img = await loadImg(url);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size + capH;
      const ctx = canvas.getContext("2d");
      if (fmt === "jpeg") {
        ctx.fillStyle = transparentBg ? "#ffffff" : bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = transparentBg ? "#ffffff" : bgColor;
        ctx.fillRect(0, size, size, capH);
      }
      ctx.drawImage(img, 0, 0, size, size);
      ctx.fillStyle = captionColor;
      ctx.font = `600 ${Math.round(capH * 0.38)}px 'JetBrains Mono', monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(caption, size / 2, size + capH * 0.54);
      return await new Promise((r) =>
        canvas.toBlob(r, fmt === "jpeg" ? "image/jpeg" : "image/png", 0.92)
      );
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async function handleDownload() {
    if (!data || !ctorRef.current || busy) return;
    setBusy(true);
    try {
      const isSvg = format === "svg";
      const inst = new ctorRef.current(
        buildOptions(exportSize, isSvg ? "svg" : "canvas")
      );
      const raw = await inst.getRawData(format);
      const blob = branding ? await brand(raw, exportSize, format) : raw;
      const ext = format === "jpeg" ? "jpg" : format;
      saveBlob(blob, `qr-${type}-${exportSize}.${ext}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("QR download failed", err);
    } finally {
      setBusy(false);
    }
  }

  /* ── Per-type form fields ───────────────────────────────────────────── */

  const field = (name, label, opts = {}) => (
    <label className={styles.field} key={name}>
      <span className={styles.fieldLabel}>{label}</span>
      {opts.textarea ? (
        <textarea
          className={styles.input}
          rows={opts.rows || 3}
          value={cur[name] || ""}
          placeholder={opts.ph}
          onChange={(e) => set(name, e.target.value)}
        />
      ) : (
        <input
          className={styles.input}
          type={opts.type || "text"}
          inputMode={opts.inputMode}
          value={cur[name] || ""}
          placeholder={opts.ph}
          onChange={(e) => set(name, e.target.value)}
        />
      )}
    </label>
  );

  const renderFields = () => {
    switch (type) {
      case "url":
        return field("url", "Website address", {
          ph: "yourbusiness.com",
          inputMode: "url",
        });
      case "text":
        return field("text", "Your text", {
          textarea: true,
          rows: 4,
          ph: "Anything you want the code to say…",
        });
      case "email":
        return (
          <>
            {field("to", "Send to", { ph: "you@business.com", type: "email" })}
            {field("subject", "Subject", { ph: "Optional" })}
            {field("body", "Message", { textarea: true, ph: "Optional" })}
          </>
        );
      case "phone":
        return field("phone", "Phone number", {
          ph: "+1 480 555 0199",
          type: "tel",
        });
      case "sms":
        return (
          <>
            {field("phone", "Phone number", { ph: "+1 480 555 0199", type: "tel" })}
            {field("message", "Pre-filled message", { textarea: true, ph: "Optional" })}
          </>
        );
      case "whatsapp":
        return (
          <>
            {field("phone", "WhatsApp number", {
              ph: "+1 480 555 0199",
              type: "tel",
            })}
            {field("message", "Pre-filled message", { textarea: true, ph: "Optional" })}
          </>
        );
      case "wifi":
        return (
          <>
            {field("ssid", "Network name (SSID)", { ph: "MyCafe_Guest" })}
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Security</span>
              <select
                className={styles.input}
                value={cur.encryption || "WPA"}
                onChange={(e) => set("encryption", e.target.value)}
              >
                <option value="WPA">WPA / WPA2 / WPA3</option>
                <option value="WEP">WEP</option>
                <option value="nopass">No password</option>
              </select>
            </label>
            {cur.encryption !== "nopass" &&
              field("password", "Password", { ph: "Network password" })}
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={!!cur.hidden}
                onChange={(e) => set("hidden", e.target.checked)}
              />
              <span>Hidden network</span>
            </label>
          </>
        );
      case "vcard":
        return (
          <>
            <div className={styles.fieldRow}>
              {field("first", "First name", { ph: "Daniel" })}
              {field("last", "Last name", { ph: "Rangel" })}
            </div>
            {field("org", "Company", { ph: "Exhale Software Solutions" })}
            {field("title", "Job title", { ph: "Owner" })}
            <div className={styles.fieldRow}>
              {field("phone", "Phone", { ph: "+1 480 555 0199", type: "tel" })}
              {field("email", "Email", { ph: "you@business.com", type: "email" })}
            </div>
            {field("website", "Website", { ph: "yourbusiness.com" })}
            {field("street", "Street address", { ph: "Optional" })}
            <div className={styles.fieldRow}>
              {field("city", "City", { ph: "Phoenix" })}
              {field("state", "State", { ph: "AZ" })}
            </div>
            <div className={styles.fieldRow}>
              {field("zip", "ZIP", { ph: "85001" })}
              {field("country", "Country", { ph: "USA" })}
            </div>
          </>
        );
      case "geo":
        return (
          <div className={styles.fieldRow}>
            {field("lat", "Latitude", { ph: "33.4484", inputMode: "decimal" })}
            {field("lng", "Longitude", { ph: "-112.0740", inputMode: "decimal" })}
          </div>
        );
      case "event":
        return (
          <>
            {field("title", "Event title", { ph: "Grand Opening" })}
            {field("location", "Location", { ph: "Optional" })}
            <div className={styles.fieldRow}>
              {field("start", "Starts", { type: "datetime-local" })}
              {field("end", "Ends", { type: "datetime-local" })}
            </div>
            {field("description", "Details", { textarea: true, ph: "Optional" })}
          </>
        );
      default:
        return null;
    }
  };

  const segmented = (options, value, onChange, opts = {}) => (
    <div className={styles.segmented}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          disabled={opts.disabled}
          className={`${styles.segBtn} ${value === o.v ? styles.segActive : ""}`}
          onClick={() => onChange(o.v)}
        >
          {o.l}
        </button>
      ))}
    </div>
  );

  const swatch = (label, value, onChange) => (
    <label className={styles.swatchField}>
      <span className={styles.swatch} style={{ background: value }}>
        <input
          type="color"
          className={styles.colorInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
      <span className={styles.swatchLabel}>{label}</span>
    </label>
  );

  return (
    <>
      <Helmet>
        <title>Free QR Code Generator — Wi-Fi, vCard, URL & More | Daniel Ray Rangel</title>
        <meta
          name="description"
          content="Free, unlimited QR code generator. Create Wi-Fi, business card (vCard), URL, email, SMS, WhatsApp, location, and event codes. Custom dot shapes, colors, and your logo. Download as PNG or SVG. No sign-up, nothing leaves your browser."
        />
        <link rel="canonical" href={`${SITE}/tools/qr`} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE}/tools/qr`} />
        <meta property="og:title" content="Free QR Code Generator — Wi-Fi, vCard, URL & More" />
        <meta
          property="og:description"
          content="Free, unlimited QR codes with custom shapes, colors, and your logo. Wi-Fi, business cards, links, and more. Download PNG or SVG. No sign-up."
        />
        <meta property="og:site_name" content="Exhale Software Solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free QR Code Generator — Wi-Fi, vCard, URL & More" />
        <meta
          name="twitter:description"
          content="Free, unlimited QR codes with custom shapes, colors, and your logo. No sign-up, nothing leaves your browser."
        />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Free QR Code Generator",
            applicationCategory: "UtilityApplication",
            operatingSystem: "Any (web browser)",
            url: `${SITE}/tools/qr`,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            featureList:
              "Wi-Fi, vCard, URL, email, SMS, WhatsApp, location, calendar QR codes; custom dot and corner shapes; custom colors; logo embedding; PNG and SVG download",
            provider: {
              "@type": "Organization",
              name: "Exhale Software Solutions",
              url: SITE,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
        {/* Mirrors the visible path back to /tools in the hero. Search results
            show the trail in place of the raw URL, so the tool reads as part of
            a collection rather than a loose page. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Free Tools", item: `${SITE}/tools` },
              {
                "@type": "ListItem",
                position: 3,
                name: "Free QR Code Generator",
                item: `${SITE}/tools/qr`,
              },
            ],
          })}
        </script>
      </Helmet>

      <main className={styles.page}>
        {/* ── HERO ── */}
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <a href="/tools" className={styles.backLink}>
              <FiArrowLeft aria-hidden="true" /> {BACK_LINK}
            </a>
            <p className={styles.eyebrow}>
              <span className={styles.eyebrowMark}>Free Tool</span>
              <span className={styles.eyebrowDot}>✦</span>
              No sign-up
              <span className={styles.eyebrowDot}>✦</span>
              Unlimited
            </p>
            <h1 className={styles.heroTitle}>
              QR codes,<br />
              <em>done right.</em>
            </h1>
            <p className={styles.heroSub}>
              Generate Wi-Fi, business-card, link, and location codes as many times
              as you want - free, forever. Shape them, color them, drop your logo
              in the middle, and download print-ready PNG or SVG. Everything happens
              right here in your browser.
            </p>
          </div>
        </section>

        {/* ── WORKSPACE ── */}
        <section className={styles.workspace}>
          <div className={styles.grid}>
            {/* Controls */}
            <div className={styles.controls}>
              <div className={styles.panel}>
                <p className={styles.panelLabel}>
                  <span className={styles.fig}>Fig. 01</span>
                  <span className={styles.figDash}>/</span> What's it for?
                </p>
                <div className={styles.typeGrid}>
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`${styles.typeBtn} ${
                          type === t.id ? styles.typeActive : ""
                        }`}
                        onClick={() => setType(t.id)}
                      >
                        <Icon className={styles.typeIcon} aria-hidden="true" />
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.panel}>
                <p className={styles.panelLabel}>
                  <span className={styles.fig}>Fig. 02</span>
                  <span className={styles.figDash}>/</span> Content
                </p>
                <div className={styles.form}>{renderFields()}</div>
              </div>

              <div className={styles.panel}>
                <p className={styles.panelLabel}>
                  <span className={styles.fig}>Fig. 03</span>
                  <span className={styles.figDash}>/</span> Style
                </p>

                <div className={styles.control}>
                  <span className={styles.controlLabel}>Dot shape</span>
                  {segmented(DOT_STYLES, dotStyle, setDotStyle)}
                </div>
                <div className={styles.control}>
                  <span className={styles.controlLabel}>Corner frame</span>
                  {segmented(CORNER_SQUARE_STYLES, cornerSquareStyle, setCornerSquareStyle)}
                </div>
                <div className={styles.control}>
                  <span className={styles.controlLabel}>Corner dot</span>
                  {segmented(CORNER_DOT_STYLES, cornerDotStyle, setCornerDotStyle)}
                </div>

                <div className={styles.control}>
                  <span className={styles.controlLabel}>Colors</span>
                  <div className={styles.swatches}>
                    {swatch("Dots", dotColor, setDotColor)}
                    {swatch("Background", bgColor, setBgColor)}
                    {accentCorners && swatch("Corners", cornerColor, setCornerColor)}
                  </div>
                  <label className={styles.checkRow}>
                    <input
                      type="checkbox"
                      checked={accentCorners}
                      onChange={(e) => setAccentCorners(e.target.checked)}
                    />
                    <span>Accent color on corners</span>
                  </label>
                  <label className={styles.checkRow}>
                    <input
                      type="checkbox"
                      checked={transparentBg}
                      onChange={(e) => setTransparentBg(e.target.checked)}
                    />
                    <span>Transparent background</span>
                  </label>
                </div>

                <div className={styles.control}>
                  <span className={styles.controlLabel}>
                    Error correction {logo && <em className={styles.forced}>· forced to H for logos</em>}
                  </span>
                  {segmented(EC_LEVELS, logo ? "H" : ecLevel, setEcLevel, {
                    disabled: !!logo,
                  })}
                </div>
              </div>

              <div className={styles.panel}>
                <p className={styles.panelLabel}>
                  <span className={styles.fig}>Fig. 04</span>
                  <span className={styles.figDash}>/</span> Logo
                </p>
                {logo ? (
                  <div className={styles.logoRow}>
                    <img src={logo} alt="Your logo" className={styles.logoThumb} />
                    <div className={styles.logoControls}>
                      <label className={styles.field}>
                        <span className={styles.fieldLabel}>
                          Size · {Math.round(logoSize * 100)}%
                        </span>
                        <input
                          type="range"
                          min="0.2"
                          max="0.55"
                          step="0.05"
                          value={logoSize}
                          onChange={(e) => setLogoSize(parseFloat(e.target.value))}
                        />
                      </label>
                      <label className={styles.checkRow}>
                        <input
                          type="checkbox"
                          checked={hideBgDots}
                          onChange={(e) => setHideBgDots(e.target.checked)}
                        />
                        <span>Clear dots behind logo</span>
                      </label>
                      <button
                        type="button"
                        className={styles.removeLogo}
                        onClick={() => setLogo(null)}
                      >
                        <FiX aria-hidden="true" /> Remove logo
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className={styles.upload}>
                    <FiUpload aria-hidden="true" />
                    <span>Upload a logo</span>
                    <span className={styles.uploadHint}>PNG or SVG, centered</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      onChange={onLogo}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className={styles.previewCol}>
              <div className={styles.previewCard}>
                <div className={styles.previewStage}>
                  <div
                    ref={previewRef}
                    className={`${styles.previewQr} ${
                      transparentBg ? styles.checker : ""
                    }`}
                    aria-label="QR code preview"
                    role="img"
                  />
                  {branding && (
                    <div className={styles.previewCaption}>
                      Made with danielrayrangel.com
                    </div>
                  )}
                </div>

                {!data && (
                  <p className={styles.previewHint}>
                    Fill in the fields above — this is a live preview of my site
                    until you do.
                  </p>
                )}

                <div className={styles.downloadBlock}>
                  <div className={styles.control}>
                    <span className={styles.controlLabel}>Format</span>
                    {segmented(
                      [
                        { v: "png", l: "PNG" },
                        { v: "svg", l: "SVG" },
                        { v: "jpeg", l: "JPG" },
                      ],
                      format,
                      setFormat
                    )}
                  </div>
                  {format !== "svg" && (
                    <div className={`${styles.control} ${styles.sizeControl}`}>
                      <span className={styles.controlLabel}>Size (px)</span>
                      {segmented(
                        EXPORT_SIZES.map((s) => ({ v: s, l: String(s) })),
                        exportSize,
                        setExportSize
                      )}
                    </div>
                  )}

                  <label className={`${styles.checkRow} ${styles.brandingRow}`}>
                    <input
                      type="checkbox"
                      checked={branding}
                      onChange={(e) => setBranding(e.target.checked)}
                    />
                    <span>Add “Made with danielrayrangel.com” caption</span>
                  </label>

                  <button
                    type="button"
                    className={styles.downloadBtn}
                    onClick={handleDownload}
                    disabled={!data || busy}
                  >
                    <FiDownload aria-hidden="true" />
                    {busy ? "Preparing…" : `Download ${format.toUpperCase()}`}
                  </button>
                  {!data && (
                    <p className={styles.downloadNote}>
                      Add your content to enable download.
                    </p>
                  )}
                  {format === "svg" && (
                    <p className={styles.downloadNote}>
                      SVG is vector — scales to any size for print with no quality loss.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section className={styles.useCases}>
          <motion.div
            className={styles.sectionHead}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className={styles.panelLabel}>
              <span className={styles.fig}>Fig. 05</span>
              <span className={styles.figDash}>/</span> Made for local business
            </p>
            <h2 className={styles.sectionTitle}>
              A code for every<br />
              <em>storefront moment.</em>
            </h2>
          </motion.div>
          <div className={styles.useGrid}>
            {USE_CASES.map((u) => {
              const Icon = u.icon;
              return (
                <motion.div
                  key={u.title}
                  className={styles.useCard}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={fadeUp}
                >
                  <Icon className={styles.useIcon} aria-hidden="true" />
                  <h3 className={styles.useTitle}>{u.title}</h3>
                  <p className={styles.useBody}>{u.body}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── Feedback ──
            Between the use cases and the fine print, so somebody who just read
            what this is for and found their case missing has somewhere to say
            so. Opens over the page rather than navigating away: the code they
            were about to describe is still on screen behind it. */}
        <section className={styles.feedback}>
          <div className={styles.feedbackInner}>
            <p className={styles.feedbackEyebrow}>{FEEDBACK.eyebrow}</p>
            <p className={styles.feedbackText}>{FEEDBACK.text}</p>
            <button
              type="button"
              className={styles.feedbackBtn}
              ref={feedbackBtnRef}
              onClick={() => setFeedbackOpen(true)}
            >
              {FEEDBACK.button} <FiArrowRight aria-hidden="true" />
            </button>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className={styles.faq}>
          <div className={styles.faqInner}>
            <motion.div
              className={styles.sectionHead}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 06</span>
                <span className={styles.figDash}>/</span> Good to know
              </p>
              <h2 className={styles.sectionTitle}>
                The honest<br />
                <em>fine print.</em>
              </h2>
            </motion.div>
            <div className={styles.faqList}>
              {FAQS.map((f, i) => (
                <details key={i} className={styles.faqItem}>
                  <summary className={styles.faqQ}>{f.q}</summary>
                  <p className={styles.faqA}>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className={styles.cta}>
          <motion.div
            className={styles.ctaInner}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className={styles.ctaEyebrow}>
              Phoenix <span className={styles.eyebrowDot}>✦</span> West Valley{" "}
              <span className={styles.eyebrowDot}>✦</span> Remote
            </p>
            <h2 className={styles.ctaTitle}>
              Need a real site<br />
              <em>behind that code?</em>
            </h2>
            <p className={styles.ctaSub}>
              A QR code is only as good as where it points. If that link should
              lead to a fast, custom website - or a code you can edit and track
              after it's printed - that's what I build.
            </p>
            <div className={styles.ctaActions}>
              <a href="/#start" className={styles.ctaPrimary}>
                Get in Touch
              </a>
              <a href="/#proof" className={styles.ctaSecondary}>
                See Recent Work
              </a>
            </div>
          </motion.div>
        </section>

        {/* Keyed so a second visit to the form is a form. ToolFeedback bails out
            of render when closed rather than unmounting, so without this the
            "sent" screen outlives the close and greets whoever opens it next. */}
        <ToolFeedback
          key={feedbackOpen ? "open" : "closed"}
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          toolName="QR Code Generator"
          labels={FEEDBACK.form}
          returnFocusTo={feedbackBtnRef}
        />
      </main>
    </>
  );
};

export default QrToolPage;
